import { useEffect, useMemo, useState } from "react";
import { Page } from "@iq/models";
import TabView from "../../../CRM/components/TabView/TabView";
import VariableTicketContainer from "../../components/VariableTicketContainer/VariableTicketContainer";
import RelatedTickets from "../../components/VariableTicketPageTabs/RelatedTickets";
import WorkNotes from "../../components/VariableTicketPageTabs/WorkNotes";
import TicketActivity from "../../components/VariableTicketPageTabs/TicketActivity";
import useProblemContext from "../../hooks/useProblemContext";
import { lifecycleStatusOptions } from "../../services/lifecycle_service";

const PROBLEM_TABS = ["Related Tickets", "Activity", "Work Notes"];

const PRIORITY_OPTIONS = ["low", "medium", "high", "critical"];
const KNOWN_ERROR_OPTIONS = [
	{ value: "true", label: "Yes" },
	{ value: "false", label: "No" },
];

function formatTimestamp(value) {
	if (!value) {
		return "-";
	}

	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) {
		return String(value);
	}

	return parsed.toLocaleString();
}

function ProblemDetail({
	problemId,
	initialProblem = null,
	teamMembers = [],
	onBack,
	onProblemUpdate,
	onProblemRefresh,
	onLinkChange,
	onLinkIncident,
	onUnlinkRelation,
	onOpenTicket,
	relationRefreshVersion = 0,
}) {
	const [problem, setProblem] = useState(initialProblem || null);
	const [statusDraft, setStatusDraft] = useState(initialProblem?.status || "new");
	const [assigneeDraft, setAssigneeDraft] = useState(
		initialProblem?.assigned_to_id != null ? String(initialProblem.assigned_to_id) : ""
	);
	const [priorityDraft, setPriorityDraft] = useState(initialProblem?.priority || "");
	const [knownErrorDraft, setKnownErrorDraft] = useState(initialProblem?.known_error ? "true" : "false");
	const [rootCauseDraft, setRootCauseDraft] = useState(initialProblem?.root_cause || "");
	const [workaroundDraft, setWorkaroundDraft] = useState(initialProblem?.workaround || "");
	const [descriptionDraft, setDescriptionDraft] = useState(initialProblem?.description || "");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [unlinkingRelationId, setUnlinkingRelationId] = useState(null);
	const [unlinkError, setUnlinkError] = useState("");
	const [activeTab, setActiveTab] = useState(PROBLEM_TABS[0]);
	const {
		context: problemContext,
		loading: loadingProblemContext,
		error: problemContextError,
		refresh: refreshProblemContext,
	} = useProblemContext(problemId);

	useEffect(() => {
		if (relationRefreshVersion > 0) {
			refreshProblemContext();
		}
	}, [relationRefreshVersion, refreshProblemContext]);

	useEffect(() => {
		setProblem(initialProblem || null);
	}, [initialProblem]);

	useEffect(() => {
		setStatusDraft(initialProblem?.status || "new");
		setAssigneeDraft(initialProblem?.assigned_to_id != null ? String(initialProblem.assigned_to_id) : "");
		setPriorityDraft(initialProblem?.priority || "");
		setKnownErrorDraft(initialProblem?.known_error ? "true" : "false");
		setRootCauseDraft(initialProblem?.root_cause || "");
		setWorkaroundDraft(initialProblem?.workaround || "");
		setDescriptionDraft(initialProblem?.description || "");
	}, [
		initialProblem?.status,
		initialProblem?.assigned_to_id,
		initialProblem?.priority,
		initialProblem?.known_error,
		initialProblem?.root_cause,
		initialProblem?.workaround,
		initialProblem?.description,
	]);

	const teamMemberMap = useMemo(
		() =>
			new Map(
				(Array.isArray(teamMembers) ? teamMembers : []).map((member) => [
					String(member?.id ?? ""),
					member?.name || member?.email || `User #${member?.id ?? "?"}`,
				])
			),
		[teamMembers]
	);

	const assigneeOptions = useMemo(
		() =>
			(Array.isArray(teamMembers) ? teamMembers : [])
				.map((member) => ({
					id: member?.id,
					label: member?.name || member?.email || `User #${member?.id ?? "?"}`,
				}))
				.filter((option) => option.id != null),
		[teamMembers]
	);

	const displayAssignee =
		problem?.assigned_to_id != null && problem?.assigned_to_id !== ""
			? teamMemberMap.get(String(problem.assigned_to_id)) || `User #${problem.assigned_to_id}`
			: "Unassigned";

	const relatedIncidentCount = problemContext?.counts?.related_incidents || 0;
	const statusOptions = lifecycleStatusOptions(problem?.status || statusDraft, problemContext?.allowed_transitions);

	function resetDraftFields() {
		setSaveError("");
		setStatusDraft(problem?.status || "new");
		setAssigneeDraft(problem?.assigned_to_id != null ? String(problem.assigned_to_id) : "");
		setPriorityDraft(problem?.priority || "");
		setKnownErrorDraft(problem?.known_error ? "true" : "false");
		setRootCauseDraft(problem?.root_cause || "");
		setWorkaroundDraft(problem?.workaround || "");
		setDescriptionDraft(problem?.description || "");
	}

	async function handleSaveFields() {
		if (!problemId || !onProblemUpdate) {
			return;
		}

		setSaving(true);
		setSaveError("");

		const payload = {
			status: statusDraft,
			assigned_to_id: assigneeDraft ? Number(assigneeDraft) : null,
			priority: priorityDraft,
			known_error: knownErrorDraft === "true",
			root_cause: rootCauseDraft,
			workaround: workaroundDraft,
			description: descriptionDraft,
		};

		try {
			const updated = await onProblemUpdate(problemId, payload);
			setProblem((prev) => ({ ...(prev || {}), ...(updated || {}) }));
			await refreshProblemContext();
		} catch (error) {
			setSaveError(error?.message || "Failed to update problem");
		} finally {
			setSaving(false);
		}
	}

	async function handleUnlink({ relationId, ticket, ticketType }) {
		if (!relationId || !onUnlinkRelation) {
			return;
		}

		setUnlinkingRelationId(relationId);
		setUnlinkError("");
		try {
			await onUnlinkRelation({
				relationId,
				tickets: [
					{ type: "problem", ticket: problem },
					{ type: ticketType, ticket },
				],
			});
			await refreshProblemContext();
		} catch (error) {
			setUnlinkError(error?.message || "Failed to unlink tickets");
		} finally {
			setUnlinkingRelationId(null);
		}
	}

	const hasUnsavedChanges =
		statusDraft !== (problem?.status || "new") ||
		assigneeDraft !== (problem?.assigned_to_id != null ? String(problem.assigned_to_id) : "") ||
		priorityDraft !== (problem?.priority || "") ||
		knownErrorDraft !== (problem?.known_error ? "true" : "false") ||
		rootCauseDraft !== (problem?.root_cause || "") ||
		workaroundDraft !== (problem?.workaround || "") ||
		descriptionDraft !== (problem?.description || "");

	const subtitle = `${relatedIncidentCount} Related Incident${relatedIncidentCount === 1 ? "" : "s"}`;

	const child1Data = {
		title: problem?.title || "Untitled Problem",
		subtitle,
		pill: problem?.status || "new",
		fields: [
			// { label: "Problem ID:", value: problem?.number },
			{ label: "Status:", value: problem?.status },
			{ label: "Priority:", value: problem?.priority },
			{ label: "Known Error:", value: problem?.known_error ? "Yes" : "No" },
			{ label: "Identified At:", value: formatTimestamp(problem?.identified_at) },
		],
		// stats: [
		// 	{ label: "Priority:", value: problem?.priority || "N/A" },
		// 	{ label: "Status:", value: problem?.status || "N/A" },
		// 	{ label: "Known Error:", value: problem?.known_error ? "Yes" : "No" },
		// ]
	};

	const child1ActionButtons = [
		{
			key: "link-incident",
			label: "Link Incident",
			onClick: () => onLinkIncident?.(problem),
			visible: Boolean(problem?.id),
			disabled: !onLinkIncident,
		},
		{
			key: "link-change",
			label: "Link Change",
			onClick: () => onLinkChange?.(problem),
			visible: Boolean(problem?.id),
			disabled: !onLinkChange,
		},
	];

	const child2Data = {
		inputs: [
			{ label: "Priority:", value: priorityDraft, placeholder: problem?.priority ?? "Unassigned", type: "dropdown", children: PRIORITY_OPTIONS, disabled: false, onChange: (value) => setPriorityDraft(value) },
			{ label: "Lifecycle Status:", value: statusDraft, placeholder: problem?.status ?? "Unassigned", type: "dropdown", children: statusOptions, disabled: false, onChange: (value) => setStatusDraft(value) },
			{ label: "Assigned To:", value: assigneeDraft, placeholder: displayAssignee, type: "dropdown", children: assigneeOptions, disabled: false, onChange: (value) => setAssigneeDraft(value) },
			{ label: "Known Error:", value: knownErrorDraft, placeholder: problem?.known_error ? "Yes" : "No", type: "dropdown", children: KNOWN_ERROR_OPTIONS, disabled: false, onChange: (value) => setKnownErrorDraft(value) },
			{ label: "Identified At:", value: formatTimestamp(problem?.identified_at), type: "text", disabled: true },
			{ label: "Resolved At:", value: formatTimestamp(problem?.resolved_at), type: "text", disabled: true },
			{ label: "Closed At:", value: formatTimestamp(problem?.closed_at), type: "text", disabled: true },
			{ label: "Root Cause:", value: rootCauseDraft, placeholder: "No root cause identified yet", type: "textarea", disabled: false, style: "grid-column: 1 / span 2;", onChange: (value) => setRootCauseDraft(value) },
			{ label: "Workaround:", value: workaroundDraft, placeholder: "No workaround documented yet", type: "textarea", disabled: false, style: "grid-column: span 2;", onChange: (value) => setWorkaroundDraft(value) },
			{ label: "Description:", value: descriptionDraft, placeholder: "No description", type: "textarea", disabled: false, style: "grid-column: 1 / span 4;", onChange: (value) => setDescriptionDraft(value) },
		],
	};

	const actionButtons = [
		{
			key: "cancel-changes",
			label: "Cancel",
			onClick: resetDraftFields,
			disabled: saving,
			variant: "secondary",
			visible: hasUnsavedChanges,
		},
		{
			key: "save-changes",
			label: saving ? "Saving..." : "Save Changes",
			onClick: handleSaveFields,
			disabled: saving,
			visible: hasUnsavedChanges,
		},
	];

	const views = {
		"Related Tickets": (
			<RelatedTickets
				data={problemContext}
				loading={loadingProblemContext}
				error={unlinkError || problemContextError}
				showProblems={false}
				onOpenTicket={onOpenTicket}
				onUnlink={handleUnlink}
				unlinkingRelationId={unlinkingRelationId}
			/>
		),
		Activity: <TicketActivity ticketType="problem" ticketId={problemId} onOpenTicket={onOpenTicket} />,
		"Work Notes": <WorkNotes ticketType="problem" ticketId={problemId} />,
	};

	const displayPageTitle = `Problem: ${problem?.number}`;

	return (
		<Page>
			{saveError ? <p style={{ color: "var(--color-danger)", marginBottom: 12 }}>{saveError}</p> : null}
			<VariableTicketContainer
				title="Problem Details"
				child1={child1Data}
				child1ActionButtons={child1ActionButtons}
				child2={child2Data}
				actionButtons={actionButtons}
				child3={
					<TabView
						tabs={PROBLEM_TABS}
						views={views}
						activeTab={activeTab}
						onTabChange={setActiveTab}
					/>
				}
			/>
		</Page>
	);
}

export default ProblemDetail;
